/* eslint-disable no-shadow */
/* eslint-disable no-console */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Flex, Loading } from "orcs-design-system";
import { useApolloClient, useQuery } from "@apollo/client";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import {
  getExistingAIResponse,
  generateNewAISummary,
} from "../../queries/ai.graphql";
import { AIMessage } from "./AIMessage";
import { createHtmlParsingOptions } from "./HTMLElementOverrides";
import {
  POLLING_INTERVAL,
  STREAMING_CHECK_DELAY,
  MAX_RETRIES,
  SOURCE_TYPES,
  GUARDRAIL_REJECTION_MESSAGE,
} from "./consts";

export const AISummary = ({
  htmlElementOverrides = {},
  sourceId,
  subjectId,
  collapsible,
}) => {
  const {
    enableAIElevatorPitchOnUserDashboard,
    enableAISummaryOnTeamDetailsPage,
    enableAISummaryOnForecastPage,
  } = useWorkspaceFeatureFlags();

  const aiEnabled = useMemo(() => {
    if (
      enableAIElevatorPitchOnUserDashboard &&
      sourceId === SOURCE_TYPES.personal_elevator_pitch
    ) {
      return true;
    }
    if (
      enableAISummaryOnTeamDetailsPage &&
      sourceId === SOURCE_TYPES.team_summary
    ) {
      return true;
    }
    if (
      enableAISummaryOnForecastPage &&
      sourceId === SOURCE_TYPES.forecast_summary
    ) {
      return true;
    }
    return false;
  }, [
    enableAIElevatorPitchOnUserDashboard,
    enableAISummaryOnTeamDetailsPage,
    enableAISummaryOnForecastPage,
    sourceId,
  ]);

  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState(false);
  const [shouldDelayStreamingCheck, setShouldDelayStreamingCheck] =
    useState(false);

  const client = useApolloClient();

  const shouldSkip = !aiEnabled || !subjectId || !sourceId;

  const {
    startPolling,
    stopPolling,
    loading,
    data,
    error: pollingError,
  } = useQuery(getExistingAIResponse, {
    variables: { sourceId, subjectId },
    fetchPolicy: "no-cache",
    skip: shouldSkip,
  });

  const createAISummary = useCallback(
    async (variables) => {
      try {
        const result = await client.mutate({
          mutation: generateNewAISummary,
          variables,
          fetchPolicy: "no-cache",
        });

        if (result?.data?.response?.startPolling) {
          startPolling(POLLING_INTERVAL);
          setShouldDelayStreamingCheck(true);

          setTimeout(() => {
            setShouldDelayStreamingCheck(false);
          }, STREAMING_CHECK_DELAY);
        }

        return result;
      } catch (e) {
        setRetryCount((prev) => prev + 1);
        setError(e);
        console.error(e);
        return {};
      }
    },
    [client, startPolling]
  );

  /**
   * Effect for starting AI conversation when component mounts
   */
  useEffect(() => {
    if (!aiEnabled) {
      return;
    }

    if (retryCount >= MAX_RETRIES) {
      setError(new Error("Max retries reached"));

      return;
    }

    if (sourceId && subjectId) {
      createAISummary({ sourceId, subjectId });
    }
  }, [sourceId, subjectId, createAISummary, aiEnabled, retryCount]);

  useEffect(() => {
    if (shouldDelayStreamingCheck) {
      return;
    }

    if (!loading && !data?.result?.streaming) {
      stopPolling();
    }
  }, [data, stopPolling, loading, shouldDelayStreamingCheck]);

  const options = createHtmlParsingOptions(htmlElementOverrides);
  const content = data?.result?.content || "";
  const isComplete = !loading && !data?.result?.streaming && content.length > 0;

  if (!aiEnabled) {
    return null;
  }

  return (
    !content.includes(GUARDRAIL_REJECTION_MESSAGE) && (
      <>
        <AIMessage
          content={content}
          options={options}
          complete={isComplete}
          collapsible={collapsible}
          errorMessage={
            error || pollingError ? (
              <>
                There was an error generating the AI summary
                {error && <div>{error.message}</div>}
                {pollingError && <div>{pollingError.message}</div>}
              </>
            ) : undefined
          }
        />
        {!isComplete && (
          <Flex p="s" justifyContent="center" alignItems="center" width="100%">
            <Loading centered variant="AIchat" />
          </Flex>
        )}
      </>
    )
  );
};

AISummary.propTypes = {
  sourceId: PropTypes.string.isRequired,
  subjectId: PropTypes.string.isRequired,
  htmlElementOverrides: PropTypes.any,
  collapsible: PropTypes.oneOf(["collapsed", "expanded"]),
};
